// Currency utilities for auto-detection and conversion
// Supports 30+ currencies with approximate exchange rates

// Exchange rates relative to USD (updated periodically)
// In production, you'd fetch these from an API
const EXCHANGE_RATES = {
  USD: 1.00,
  CAD: 1.36,
  EUR: 0.92,
  GBP: 0.79,
  AUD: 1.53,
  INR: 83.12,
  JPY: 149.50,
  CNY: 7.24,
  BRL: 4.97,
  MXN: 17.15,
  KRW: 1330.00,
  SGD: 1.34,
  HKD: 7.82,
  NZD: 1.64,
  SEK: 10.42,
  NOK: 10.55,
  DKK: 6.87,
  CHF: 0.88,
  ZAR: 18.65,
  AED: 3.67,
  SAR: 3.75,
  PHP: 56.20,
  THB: 35.50,
  MYR: 4.72,
  IDR: 15650.00,
  PLN: 3.98,
  CZK: 23.20,
  HUF: 358.00,
  ILS: 3.65,
  TRY: 32.10,
  RUB: 92.50,
  PKR: 279.00,
  NGN: 1550.00,
  EGP: 30.90,
  VND: 24500.00,
  CLP: 980.00,
  COP: 3950.00,
  PEN: 3.72,
  ARS: 850.00,
};

// Currency symbols and formatting
const CURRENCY_INFO = {
  USD: { symbol: '$', name: 'US Dollar', locale: 'en-US' },
  CAD: { symbol: 'CA$', name: 'Canadian Dollar', locale: 'en-CA' },
  EUR: { symbol: '€', name: 'Euro', locale: 'de-DE' },
  GBP: { symbol: '£', name: 'British Pound', locale: 'en-GB' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', locale: 'en-AU' },
  INR: { symbol: '₹', name: 'Indian Rupee', locale: 'en-IN' },
  JPY: { symbol: '¥', name: 'Japanese Yen', locale: 'ja-JP', decimals: 0 },
  CNY: { symbol: '¥', name: 'Chinese Yuan', locale: 'zh-CN' },
  BRL: { symbol: 'R$', name: 'Brazilian Real', locale: 'pt-BR' },
  MXN: { symbol: 'MX$', name: 'Mexican Peso', locale: 'es-MX' },
  KRW: { symbol: '₩', name: 'South Korean Won', locale: 'ko-KR', decimals: 0 },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', locale: 'en-SG' },
  HKD: { symbol: 'HK$', name: 'Hong Kong Dollar', locale: 'zh-HK' },
  NZD: { symbol: 'NZ$', name: 'New Zealand Dollar', locale: 'en-NZ' },
  SEK: { symbol: 'kr', name: 'Swedish Krona', locale: 'sv-SE' },
  NOK: { symbol: 'kr', name: 'Norwegian Krone', locale: 'nb-NO' },
  DKK: { symbol: 'kr', name: 'Danish Krone', locale: 'da-DK' },
  CHF: { symbol: 'CHF', name: 'Swiss Franc', locale: 'de-CH' },
  ZAR: { symbol: 'R', name: 'South African Rand', locale: 'en-ZA' },
  AED: { symbol: 'د.إ', name: 'UAE Dirham', locale: 'ar-AE' },
  SAR: { symbol: '﷼', name: 'Saudi Riyal', locale: 'ar-SA' },
  PHP: { symbol: '₱', name: 'Philippine Peso', locale: 'en-PH' },
  THB: { symbol: '฿', name: 'Thai Baht', locale: 'th-TH' },
  MYR: { symbol: 'RM', name: 'Malaysian Ringgit', locale: 'ms-MY' },
  IDR: { symbol: 'Rp', name: 'Indonesian Rupiah', locale: 'id-ID', decimals: 0 },
  PLN: { symbol: 'zł', name: 'Polish Zloty', locale: 'pl-PL' },
  CZK: { symbol: 'Kč', name: 'Czech Koruna', locale: 'cs-CZ' },
  HUF: { symbol: 'Ft', name: 'Hungarian Forint', locale: 'hu-HU', decimals: 0 },
  ILS: { symbol: '₪', name: 'Israeli Shekel', locale: 'he-IL' },
  TRY: { symbol: '₺', name: 'Turkish Lira', locale: 'tr-TR' },
  PKR: { symbol: 'Rs', name: 'Pakistani Rupee', locale: 'en-PK', decimals: 0 },
  NGN: { symbol: '₦', name: 'Nigerian Naira', locale: 'en-NG' },
  EGP: { symbol: 'E£', name: 'Egyptian Pound', locale: 'ar-EG' },
  VND: { symbol: '₫', name: 'Vietnamese Dong', locale: 'vi-VN', decimals: 0 },
  CLP: { symbol: 'CL$', name: 'Chilean Peso', locale: 'es-CL', decimals: 0 },
  COP: { symbol: 'CO$', name: 'Colombian Peso', locale: 'es-CO', decimals: 0 },
  PEN: { symbol: 'S/', name: 'Peruvian Sol', locale: 'es-PE' },
  ARS: { symbol: 'AR$', name: 'Argentine Peso', locale: 'es-AR' },
};

// Country to currency mapping
const COUNTRY_CURRENCY = {
  US: 'USD', CA: 'CAD', GB: 'GBP', AU: 'AUD', NZ: 'NZD',
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR', PT: 'EUR', IE: 'EUR', FI: 'EUR', GR: 'EUR',
  IN: 'INR', JP: 'JPY', CN: 'CNY', KR: 'KRW', SG: 'SGD', HK: 'HKD', TW: 'TWD', TH: 'THB', MY: 'MYR', ID: 'IDR', PH: 'PHP', VN: 'VND',
  BR: 'BRL', MX: 'MXN', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN',
  SE: 'SEK', NO: 'NOK', DK: 'DKK', CH: 'CHF', PL: 'PLN', CZ: 'CZK', HU: 'HUF',
  ZA: 'ZAR', NG: 'NGN', EG: 'EGP', AE: 'AED', SA: 'SAR', IL: 'ILS', TR: 'TRY', PK: 'PKR', RU: 'RUB',
};

// Timezone to country mapping (approximate)
const TIMEZONE_COUNTRY = {
  'America/New_York': 'US', 'America/Los_Angeles': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
  'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Montreal': 'CA',
  'Europe/London': 'GB', 'Europe/Paris': 'FR', 'Europe/Berlin': 'DE', 'Europe/Rome': 'IT', 'Europe/Madrid': 'ES',
  'Europe/Amsterdam': 'NL', 'Europe/Brussels': 'BE', 'Europe/Vienna': 'AT', 'Europe/Lisbon': 'PT',
  'Europe/Dublin': 'IE', 'Europe/Helsinki': 'FI', 'Europe/Athens': 'GR',
  'Europe/Stockholm': 'SE', 'Europe/Oslo': 'NO', 'Europe/Copenhagen': 'DK', 'Europe/Zurich': 'CH',
  'Europe/Warsaw': 'PL', 'Europe/Prague': 'CZ', 'Europe/Budapest': 'HU',
  'Asia/Tokyo': 'JP', 'Asia/Shanghai': 'CN', 'Asia/Hong_Kong': 'HK', 'Asia/Singapore': 'SG',
  'Asia/Seoul': 'KR', 'Asia/Taipei': 'TW', 'Asia/Bangkok': 'TH', 'Asia/Jakarta': 'ID',
  'Asia/Kuala_Lumpur': 'MY', 'Asia/Manila': 'PH', 'Asia/Ho_Chi_Minh': 'VN',
  'Asia/Kolkata': 'IN', 'Asia/Mumbai': 'IN', 'Asia/Delhi': 'IN',
  'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA', 'Asia/Jerusalem': 'IL', 'Asia/Istanbul': 'TR', 'Asia/Karachi': 'PK',
  'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Brisbane': 'AU', 'Australia/Perth': 'AU',
  'Pacific/Auckland': 'NZ',
  'America/Sao_Paulo': 'BR', 'America/Mexico_City': 'MX', 'America/Buenos_Aires': 'AR',
  'America/Santiago': 'CL', 'America/Bogota': 'CO', 'America/Lima': 'PE',
  'Africa/Johannesburg': 'ZA', 'Africa/Lagos': 'NG', 'Africa/Cairo': 'EG',
  'Europe/Moscow': 'RU',
};

/**
 * Detect user's country from timezone
 */
export const detectCountry = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_COUNTRY[timezone] || 'US'; // Default to US
  } catch {
    return 'US';
  }
};

/**
 * Get currency code for a country
 */
export const getCurrencyForCountry = (countryCode) => {
  return COUNTRY_CURRENCY[countryCode] || 'USD';
};

/**
 * Detect user's currency based on their location
 */
export const detectCurrency = () => {
  const country = detectCountry();
  return getCurrencyForCountry(country);
};

/**
 * Convert USD amount to local currency
 */
export const convertFromUSD = (usdAmount, targetCurrency) => {
  const rate = EXCHANGE_RATES[targetCurrency] || 1;
  return usdAmount * rate;
};

/**
 * Format price in local currency
 */
export const formatPrice = (amount, currencyCode) => {
  const info = CURRENCY_INFO[currencyCode] || CURRENCY_INFO.USD;
  const decimals = info.decimals !== undefined ? info.decimals : 2;
  
  // Round to appropriate decimals
  const roundedAmount = decimals === 0 
    ? Math.round(amount) 
    : Math.round(amount * 100) / 100;
  
  try {
    return new Intl.NumberFormat(info.locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(roundedAmount);
  } catch {
    // Fallback formatting
    return `${info.symbol}${roundedAmount.toFixed(decimals)}`;
  }
};

/**
 * Get localized price from USD
 */
export const getLocalizedPrice = (usdAmount, currencyCode = null) => {
  const currency = currencyCode || detectCurrency();
  const localAmount = convertFromUSD(usdAmount, currency);
  return {
    currency,
    amount: localAmount,
    formatted: formatPrice(localAmount, currency),
    usdAmount,
    usdFormatted: formatPrice(usdAmount, 'USD'),
  };
};

/**
 * Get currency info
 */
export const getCurrencyInfo = (currencyCode) => {
  return CURRENCY_INFO[currencyCode] || CURRENCY_INFO.USD;
};

/**
 * Get currency based on language code
 */
export const getCurrencyForLanguage = (languageCode) => {
  const LANGUAGE_CURRENCY = {
    en: 'USD',
    hi: 'INR',
    es: 'EUR',  // Spanish - Euro (Spain)
    fr: 'EUR',  // French - Euro
    de: 'EUR',  // German - Euro
    pt: 'BRL',  // Portuguese - Brazilian Real
    zh: 'CNY',  // Chinese - Yuan
    ja: 'JPY',  // Japanese - Yen
    ko: 'KRW',  // Korean - Won
    ar: 'SAR',  // Arabic - Saudi Riyal
    ru: 'RUB',  // Russian - Ruble
  };
  return LANGUAGE_CURRENCY[languageCode] || 'USD';
};

/**
 * Get all supported currencies for dropdown
 */
export const getSupportedCurrencies = () => {
  return Object.entries(CURRENCY_INFO).map(([code, info]) => ({
    code,
    ...info,
  }));
};

// Localized pricing - set specific prices per region instead of just converting
// This allows for market-appropriate pricing
const LOCALIZED_PRICING = {
  USD: { monthly: 2.99, annual: 12.99, lifetime: 49.99 },
  INR: { monthly: 125, annual: 1299, lifetime: 4155 },      // India - value pricing
  EUR: { monthly: 2.49, annual: 9.99, lifetime: 39.99 },    // Europe
  GBP: { monthly: 1.99, annual: 8.99, lifetime: 34.99 },    // UK
  CAD: { monthly: 3.99, annual: 14.99, lifetime: 59.99 },   // Canada
  AUD: { monthly: 4.49, annual: 16.99, lifetime: 64.99 },   // Australia
  JPY: { monthly: 350, annual: 1500, lifetime: 5500 },      // Japan
  CNY: { monthly: 15, annual: 79, lifetime: 299 },          // China
  KRW: { monthly: 3500, annual: 15000, lifetime: 55000 },   // Korea
  BRL: { monthly: 9.99, annual: 49.99, lifetime: 149.99 },  // Brazil
  MXN: { monthly: 49, annual: 199, lifetime: 799 },         // Mexico
  SAR: { monthly: 10, annual: 49, lifetime: 179 },          // Saudi Arabia
  AED: { monthly: 10, annual: 49, lifetime: 179 },          // UAE
  RUB: { monthly: 199, annual: 999, lifetime: 3999 },       // Russia
  PKR: { monthly: 499, annual: 2499, lifetime: 9999 },      // Pakistan
  SGD: { monthly: 3.99, annual: 14.99, lifetime: 59.99 },   // Singapore
};

/**
 * Get localized price for a plan
 * Uses region-specific pricing if available, otherwise converts from USD
 */
export const getLocalizedPriceForPlan = (plan, currencyCode) => {
  const localPricing = LOCALIZED_PRICING[currencyCode];
  
  if (localPricing && localPricing[plan]) {
    // Use the localized price directly
    const price = localPricing[plan];
    const usdPrice = LOCALIZED_PRICING.USD[plan] || PRICING.healthApp[plan]?.price || 0;
    return {
      currency: currencyCode,
      amount: price,
      formatted: formatPrice(price, currencyCode),
      usdAmount: usdPrice,
      usdFormatted: formatPrice(usdPrice, 'USD'),
    };
  }
  
  // Fall back to USD conversion
  const usdPrice = LOCALIZED_PRICING.USD[plan] || PRICING.healthApp[plan]?.price || 0;
  return getLocalizedPrice(usdPrice, currencyCode);
};

// Pricing tiers in USD (base prices)
export const PRICING = {
  emergencyCard: {
    price: 1,
    name: 'Emergency Card',
    description: 'One-time purchase',
  },
  healthApp: {
    monthly: {
      price: 2.99,
      name: 'Monthly',
      description: 'Billed monthly',
      interval: 'month',
    },
    annual: {
      price: 12.99,
      name: 'Annual',
      description: 'Billed yearly (Save 64%)',
      interval: 'year',
      savings: '64%',
    },
    lifetime: {
      price: 49.99,
      name: 'Lifetime',
      description: 'One-time purchase',
      interval: 'once',
      badge: 'Best Value',
    },
  },
};

export default {
  detectCountry,
  detectCurrency,
  convertFromUSD,
  formatPrice,
  getLocalizedPrice,
  getLocalizedPriceForPlan,
  getCurrencyInfo,
  getCurrencyForLanguage,
  getSupportedCurrencies,
  PRICING,
  LOCALIZED_PRICING,
};
